import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const toSafeMillimeters = (value, fallback, min = 15, max = 120) => {
  const size = Number(value);
  if (!Number.isFinite(size)) return fallback;
  return Math.min(Math.max(size, min), max);
};

const buildBarcodeSvg = (value) => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  JsBarcode(svg, String(value), {
    format: "CODE128",
    height: 34,
    width: 1,
    displayValue: false,
    margin: 0,
    background: "#ffffff",
    lineColor: "#000000",
  });
  return svg.outerHTML;
};

/**
 * Print a medicine shelf label with barcode, QR code, or both.
 * @param {{ barcode: string, name?: string, category?: string, price?: string|number, expiry?: string, mode?: 'barcode'|'qr'|'both', copies?: number, widthMm?: number, heightMm?: number }} options
 */
export const printMedicineLabel = async ({
  barcode,
  name,
  category,
  price,
  expiry,
  mode = "both",
  copies = 1,
  // XP-T361U supports labels up to 80 mm wide. 80 x 50 mm is landscape:
  // the barcode and QR code print beside each other, not one above the other.
  widthMm = 80,
  heightMm = 50,
}) => {
  const code = String(barcode || "").trim();
  if (!code) return false;

  // XP-T361U's supported print width is 20–80 mm.
  const labelWidthMm = toSafeMillimeters(widthMm, 80, 20, 80);
  const labelHeightMm = toSafeMillimeters(heightMm, 50);
  const safeMode = ["barcode", "qr", "both"].includes(mode) ? mode : "both";
  const safeName = escapeHtml(name || code);
  const safeBarcode = escapeHtml(code);
  const safeCategory = category ? escapeHtml(category) : "";
  const safePrice = price != null && price !== "" ? escapeHtml(price) : "";
  const safeExpiry = expiry ? escapeHtml(expiry) : "";

  const showBarcode = safeMode === "barcode" || safeMode === "both";
  const showQr = safeMode === "qr" || safeMode === "both";

  const barcodeSvg = showBarcode ? buildBarcodeSvg(code) : "";
  const qrDataUrl = showQr
    ? await QRCode.toDataURL(code, {
        width: 92,
        margin: 1,
        color: { dark: "#000000", light: "#ffffff" },
      })
    : "";

  const metaRows = [
    safeCategory ? `<div class="meta">${safeCategory}</div>` : "",
    safePrice ? `<div class="meta">${safePrice}</div>` : "",
    safeExpiry ? `<div class="meta">${safeExpiry}</div>` : "",
  ]
    .filter(Boolean)
    .join("");

  const labelBody = `
    <div class="label mode-${safeMode}">
      <div class="title">${safeName}</div>
      ${metaRows}
      <div class="codes">
        ${showBarcode ? `<div class="barcode">${barcodeSvg}</div>` : ""}
        ${showQr ? `<img class="qr" src="${qrDataUrl}" alt="QR ${safeBarcode}" />` : ""}
      </div>
      <div class="code-text">${safeBarcode}</div>
    </div>
  `;

  const labelCount = Math.max(1, Number.parseInt(copies, 10) || 1);
  const labels = Array.from({ length: labelCount }, () => labelBody).join("");

  const win = window.open("", "medicine-label-print");
  if (!win) return false;

  win.document.write(`
    <html>
      <head>
        <title>${safeName}</title>
        <style>
          @page { size: ${labelWidthMm}mm ${labelHeightMm}mm; margin: 0; }
          * { box-sizing: border-box; }
          html {
            width: ${labelWidthMm}mm;
            height: ${labelHeightMm}mm;
            margin: 0;
            padding: 0;
            background: #ffffff;
          }
          body {
            width: ${labelWidthMm}mm;
            height: auto;
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            color: #000000;
            background: #ffffff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .label {
            width: ${labelWidthMm}mm;
            height: ${labelHeightMm}mm;
            padding: 1mm 1.5mm;
            overflow: hidden;
            text-align: center;
            background: #ffffff;
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: always;
            break-after: page;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: stretch;
            gap: 0.35mm;
          }
          .label:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          .title {
            flex: 0 0 auto;
            font-size: 7.2px;
            font-weight: 700;
            line-height: 1.05;
            max-height: 5.2mm;
            overflow: hidden;
            overflow-wrap: anywhere;
          }
          .meta {
            flex: 0 0 auto;
            font-size: 5.2px;
            line-height: 1;
            color: #111111;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .codes {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1mm;
            flex-wrap: nowrap;
            flex: 1 1 auto;
            min-height: 0;
            overflow: hidden;
          }
          .barcode {
            flex: 1 1 auto;
            min-width: 0;
            overflow: hidden;
            line-height: 0;
          }
          .barcode svg {
            display: block;
            width: 100%;
            max-width: 100%;
            height: 10mm;
          }
          .qr {
            flex: 0 0 auto;
            width: min(11mm, 44%);
            height: min(11mm, 44%);
            display: block;
          }
          .code-text {
            flex: 0 0 auto;
            font-size: 5px;
            line-height: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .mode-barcode .barcode svg {
            height: 14mm;
          }
          .mode-barcode .codes {
            min-height: 14mm;
          }
          .mode-barcode .code-text,
          .mode-qr .code-text {
            font-size: 5.8px;
          }
          .mode-qr .qr {
            width: min(15mm, 70%);
            height: min(15mm, 70%);
          }
        </style>
      </head>
      <body>${labels}<script>window.onload = () => { window.print(); window.close(); }<\/script></body>
    </html>
  `);
  win.document.close();
  return true;
};

export default printMedicineLabel;
